/**
 * Code Formatter Utilities
 *
 * Simple formatting for JavaScript, HTML, and CSS code to make it more readable for teachers.
 * Uses basic rules for proper indentation and line breaks.
 */

/**
 * Format JavaScript code for better readability
 * - Add newlines after semicolons
 * - Indent nested blocks
 * - Add spacing around operators
 */
export function formatJavaScript(code: string): string {
  if (!code || typeof code !== 'string') return code;

  try {
    let formatted = code;

    // Add newline after semicolons (but not in for loops)
    formatted = formatted.replace(/;(?=\s*[a-z])/gi, ';\n');

    // Add newlines and proper spacing around braces
    formatted = formatted.replace(/\{/g, ' {\n');
    formatted = formatted.replace(/\}/g, '\n}\n');

    // Add space around assignment operators
    formatted = formatted.replace(/([^=!<>])=(?!=)/g, '$1 = ');
    formatted = formatted.replace(/\+=(?!\s)/g, ' += ');
    formatted = formatted.replace(/-=(?!\s)/g, ' -= ');

    // Add space after commas
    formatted = formatted.replace(/,(?!\s)/g, ', ');

    // Apply basic indentation
    const lines = formatted.split('\n');
    let indentLevel = 0;
    const indentedLines = lines.map(line => {
      const trimmed = line.trim();
      if (!trimmed) return '';

      // Decrease indent for closing braces
      if (trimmed.startsWith('}')) {
        indentLevel = Math.max(0, indentLevel - 1);
      }

      const indented = '  '.repeat(indentLevel) + trimmed;

      // Increase indent for opening braces
      if (trimmed.endsWith('{')) {
        indentLevel++;
      }

      return indented;
    });

    formatted = indentedLines.join('\n').replace(/\n{3,}/g, '\n\n').trim();
    return formatted;
  } catch (error) {
    console.warn('Failed to format JavaScript:', error);
    return code;
  }
}

/**
 * Format HTML code for better readability
 */
export function formatHTML(code: string): string {
  if (!code || typeof code !== 'string') return code;

  try {
    let formatted = code;

    // Add newlines after tags
    formatted = formatted.replace(/>/g, '>\n');
    formatted = formatted.replace(/<\//g, '\n</');

    // Apply basic indentation
    const lines = formatted.split('\n');
    let indentLevel = 0;
    const indentedLines = lines.map(line => {
      const trimmed = line.trim();
      if (!trimmed) return '';

      // Decrease indent for closing tags
      if (trimmed.startsWith('</')) {
        indentLevel = Math.max(0, indentLevel - 1);
      }

      const indented = '  '.repeat(indentLevel) + trimmed;

      // Increase indent for opening tags (not self-closing or closing tags)
      if (trimmed.startsWith('<') && !trimmed.startsWith('</') && !trimmed.endsWith('/>')) {
        // Check if it's a complete tag or just opening
        if (!trimmed.includes('</')) {
          indentLevel++;
        }
      }

      return indented;
    });

    formatted = indentedLines.join('\n').replace(/\n{3,}/g, '\n\n').trim();
    return formatted;
  } catch (error) {
    console.warn('Failed to format HTML:', error);
    return code;
  }
}

/**
 * Format CSS code for better readability
 */
export function formatCSS(code: string): string {
  if (!code || typeof code !== 'string') return code;

  try {
    let formatted = code;

    // Add newlines after braces and semicolons
    formatted = formatted.replace(/\{/g, ' {\n');
    formatted = formatted.replace(/\}/g, '\n}\n');
    formatted = formatted.replace(/;/g, ';\n');

    // Apply basic indentation
    const lines = formatted.split('\n');
    let indentLevel = 0;
    const indentedLines = lines.map(line => {
      const trimmed = line.trim();
      if (!trimmed) return '';

      if (trimmed === '}') {
        indentLevel = Math.max(0, indentLevel - 1);
      }

      const indented = '  '.repeat(indentLevel) + trimmed;

      if (trimmed.endsWith('{')) {
        indentLevel++;
      }

      return indented;
    });

    formatted = indentedLines.join('\n').replace(/\n{3,}/g, '\n\n').trim();
    return formatted;
  } catch (error) {
    console.warn('Failed to format CSS:', error);
    return code;
  }
}

/**
 * Auto-detect code type and format accordingly
 */
export function formatCode(code: string, type?: 'javascript' | 'html' | 'css' | 'text'): string {
  if (!code || typeof code !== 'string') return code;

  // If type is explicitly provided, use it
  if (type) {
    switch (type) {
      case 'javascript':
        return formatJavaScript(code);
      case 'html':
        return formatHTML(code);
      case 'css':
        return formatCSS(code);
      default:
        return code;
    }
  }

  // Auto-detect based on content
  if (code.includes('<!DOCTYPE') || code.includes('<html') || code.includes('<body')) {
    return formatHTML(code);
  } else if (code.includes('function') || code.includes('var ') || code.includes('let ') || code.includes('const ')) {
    return formatJavaScript(code);
  } else if (code.includes('{') && code.includes(':') && code.includes(';')) {
    return formatCSS(code);
  }

  return code;
}
